import type { User } from "firebase/auth";
import { deleteDoc, doc, serverTimestamp, setDoc } from "firebase/firestore";
import { deleteToken, getMessaging, getToken, isSupported, onMessage, type Messaging } from "firebase/messaging";
import { db, firebaseApp } from "./firebase";

let messagingInstance: Messaging | null = null;
let supportChecked: Promise<boolean> | null = null;

export function isPushConfigured(): boolean {
  return Boolean(import.meta.env.VITE_FIREBASE_VAPID_KEY);
}

export async function isPushSupported(): Promise<boolean> {
  if (typeof window === "undefined" || !("Notification" in window) || !firebaseApp) {
    return false;
  }

  if (!supportChecked) {
    supportChecked = isSupported().catch(() => false);
  }

  return supportChecked;
}

async function getMessagingInstance(): Promise<Messaging | null> {
  if (!firebaseApp || !(await isPushSupported())) {
    return null;
  }

  if (!messagingInstance) {
    messagingInstance = getMessaging(firebaseApp);
  }

  return messagingInstance;
}

export async function registerPushNotifications(user: User): Promise<void> {
  const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
  if (!vapidKey) {
    throw new Error("Notificações push não estão configuradas neste ambiente.");
  }

  const messaging = await getMessagingInstance();
  if (!messaging) {
    throw new Error("Notificações push não são suportadas neste navegador.");
  }

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("Permissão de notificação não concedida.");
  }

  const swRegistration = await navigator.serviceWorker.ready;
  const token = await getToken(messaging, { vapidKey, serviceWorkerRegistration: swRegistration });
  if (!token || !db) {
    throw new Error("Não foi possível gerar o token de notificação neste dispositivo.");
  }

  await setDoc(doc(db, "users", user.uid, "fcmTokens", token), {
    createdAt: serverTimestamp(),
    userAgent: navigator.userAgent
  }, { merge: true });
}

export async function unregisterPushNotifications(user: User): Promise<void> {
  const messaging = await getMessagingInstance();
  const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
  if (!messaging || !db || !vapidKey) {
    return;
  }

  try {
    const token = await getToken(messaging, { vapidKey });
    if (token) {
      await deleteToken(messaging);
      await deleteDoc(doc(db, "users", user.uid, "fcmTokens", token));
    }
  } catch {
    // Sem token ativo neste dispositivo; nada a remover.
  }
}

export async function subscribeForegroundPush(onNotification: (title: string, body: string) => void): Promise<() => void> {
  const messaging = await getMessagingInstance();
  if (!messaging) {
    return () => undefined;
  }

  return onMessage(messaging, (payload) => {
    onNotification(payload.notification?.title ?? "IMOBI", payload.notification?.body ?? "");
  });
}
